import { useStripe } from '@stripe/react-stripe-js';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { usePaymentUpdate, useSubscribeUser } from '../../hooks';
import { root_path, subscribe_referral_path } from '../../routes.js';
import { extractQueryParams } from '../../utils/queryStringHelper';
import { CustomSpinner } from '../BaseUI';
import styles from './styles.module.scss';

enum StripeStatus {
  Succeeded = 'succeeded',
  Processing = 'processing',
  RequiresPaymentMethod = 'requires_payment_method',
}

const PaymentStatus = () => {
  const stripe = useStripe();
  const [message, setMessage] = useState(null);
  const [subMessage, setSubMessage] = useState(null);
  const { mutate: subscribeUser } = useSubscribeUser();
  const { mutate: updatePayment } = usePaymentUpdate();
  const {
    setup_intent_client_secret: clientSecret,
    setup_intent: setupIntentId,
    code: offerCode,
    guid: referralGuid,
    modal: modalGuid,
    update_payment: isUpdatePayment,
    article_id: articleId,
  } = extractQueryParams([
    'setup_intent_client_secret',
    'setup_intent',
    'code',
    'guid',
    'modal',
    'update_payment',
    'article_id',
  ]);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    // User shouldn't be on this page if they don't have these params
    const userShouldntAccessConfirmationPage =
      !clientSecret || !setupIntentId || (!offerCode && !isUpdatePayment);
    if (userShouldntAccessConfirmationPage) {
      toast.error("You don't have permission to access this page.");
      window.location = root_path();
      return;
    }

    stripe.retrieveSetupIntent(clientSecret).then(({ setupIntent, error }) => {
      if (error) {
        toast.error('Something went wrong, please try again');
        window.location.href = root_path();
        return;
      }

      switch (setupIntent.status) {
        case StripeStatus.Succeeded:
          setMessage('Confirming payment method.');
          setSubMessage('Please hold while your payment is being processed.');
          if (isUpdatePayment) {
            updatePayment();
          } else {
            subscribeUser({
              modal: modalGuid,
              page: window.location.pathname,
              code: offerCode,
              referring_article_id: articleId,
              referrer_guid: referralGuid,
              source: 'web',
            });
          }
          break;

        case StripeStatus.Processing:
          setMessage('Processing payment details.');
          setSubMessage("We'll update you when processing is complete.");
          break;

        case StripeStatus.RequiresPaymentMethod:
          // Redirect your user back to your payment page to attempt collecting payment again
          setMessage('Payment method failed.');
          setSubMessage('Please hold while we redirect you back to try another payment method.');
          setTimeout(() => {
            if (referralGuid) {
              window.location.href = subscribe_referral_path(referralGuid);
            } else {
              history.back();
            }
          }, 5000);
          break;
      }
    });
  }, [
    stripe,
    clientSecret,
    offerCode,
    setupIntentId,
    referralGuid,
    modalGuid,
    isUpdatePayment,
    subscribeUser,
    updatePayment,
    articleId,
  ]);

  return (
    <div className={clsx(styles.confirmationContainer, 'container')}>
      <div className="row">
        <div className="col-md-10 col-md-offset-1">
          <div className={styles.confirmationCard}>
            <div className={styles.loader}>
              <CustomSpinner color="#d04237" size={15} />
            </div>
            <div className={styles.message}>{message}</div>
            <div className={styles.subMessage}>{subMessage}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentStatus;
